import { Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Table, TableWrapper } from "@/components/ui/table";
import type { ResponseBlock } from "./types";

const TONE_TO_BADGE: Record<string, "success" | "warning" | "danger" | "info" | "pending" | "neutral"> = {
  success: "success",
  warning: "warning",
  danger: "danger",
  info: "info",
  pending: "pending",
  neutral: "neutral",
};

function KpiRow({ items }: { items: { label: string; value: string; hint?: string | null }[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {items.map((item) => (
        <div key={item.label} className="rounded-lg border border-line bg-surface p-3">
          <div className="text-lg font-bold text-ink">{item.value}</div>
          <div className="mt-0.5 text-xs text-muted">{item.label}</div>
          {item.hint && <div className="mt-1 text-xs font-medium text-success">{item.hint}</div>}
        </div>
      ))}
    </div>
  );
}

function BreakdownList({ title, items }: { title?: string; items: { label: string; pct: number; value: string }[] }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      {title && <div className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">{title}</div>}
      <div className="flex flex-col gap-2.5">
        {items.map((item) => (
          <div key={item.label}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-text">{item.label}</span>
              <span className="text-muted">{item.value} · {item.pct}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-bg">
              <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(item.pct, 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusList({ items }: { items: { label: string; meta: string; tone: string; statusLabel: string }[] }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-ink">{item.label}</div>
            <div className="truncate text-xs text-muted">{item.meta}</div>
          </div>
          <Badge variant={TONE_TO_BADGE[item.tone] ?? "neutral"} size="sm" dot={false} className="shrink-0">
            {item.statusLabel}
          </Badge>
        </div>
      ))}
    </div>
  );
}

function DataTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  return (
    <TableWrapper>
      <Table className="dense">
        <thead>
          <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
            {columns.map((c) => (
              <th key={c} className="px-3 py-2">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-line text-sm text-text last:border-b-0">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
}

function Insight({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-dashed border-line-strong bg-bg p-3">
      <Sparkles aria-hidden className="mt-0.5 size-3.5 shrink-0 text-primary" />
      <div>
        <span className="mr-1.5 inline-flex items-center rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
          Estimated
        </span>
        <span className="text-sm leading-relaxed text-text">{text}</span>
      </div>
    </div>
  );
}

export function ResponseBlockView({ block }: { block: ResponseBlock }) {
  switch (block.kind) {
    case "kpi":
      return <KpiRow items={block.items} />;
    case "breakdown":
      return <BreakdownList title={block.title} items={block.items} />;
    case "statusList":
      return <StatusList items={block.items} />;
    case "table":
      return <DataTable columns={block.columns} rows={block.rows} />;
    case "insight":
      return <Insight text={block.text} />;
    default:
      return null;
  }
}
